import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from 'api';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useUndoActions from 'hooks/useUndoActions';
import {
  SUGGESTED_ACTION_MUTE_DAYS,
  SuggestedActionType,
} from 'router/subrouters/DataRoom/models/Dashboard';
import { refetchEnterpriseSuggestedActions } from '../utils';

type DismissedAction = {
  type: SuggestedActionType;
  dataRoomId: string;
};

export type EnterpriseSuggestedActionUndoHandlers = {
  setDismissedAction: (action: DismissedAction) => void;
  setDismissUndoTimeout: () => void;
  setDontRemindUndoTimeout: () => void;
  setMutedActionType: (type: SuggestedActionType) => void;
  setMuteForAllDealsUndoTimeout: () => void;
  resetPage: () => void;
};

const useEnterpriseUndoActions = (resetPage: () => void) => {
  const [dismissedAction, setDismissedAction] = useState<DismissedAction>();
  const [mutedActionType, setMutedActionType] = useState<SuggestedActionType>();

  const {
    isUndoAvailable: isDismissUndoAvailable,
    setUndoTimeout: setDismissUndoTimeout,
    clearUndoTimeout: clearDismissUndoTimeout,
  } = useUndoActions();

  const {
    isUndoAvailable: isDontRemindUndoAvailable,
    setUndoTimeout: setDontRemindUndoTimeout,
    clearUndoTimeout: clearDontRemindUndoTimeout,
  } = useUndoActions();

  const {
    isUndoAvailable: isMuteForAllUndoAvailable,
    setUndoTimeout: setMuteForAllDealsUndoTimeout,
    clearUndoTimeout: clearMuteForAllUndoTimeout,
  } = useUndoActions();

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [onUndoDismiss, isRestoringDismiss] = useCallbackAsync(async () => {
    if (dismissedAction) {
      await api.enterpriseSuggestedActions.restoreSuggestedAction(
        dismissedAction.dataRoomId,
        dismissedAction.type,
      );
    }

    await refetchEnterpriseSuggestedActions(queryClient);
    clearDismissUndoTimeout();
    clearDontRemindUndoTimeout();
  });

  const [onUndoMuteForAllDeals, isRestoringMuteForAll] = useCallbackAsync(
    async () => {
      await api.enterpriseSuggestedActions.restoreSuggestedActionForAllDeals(
        mutedActionType,
      );

      await refetchEnterpriseSuggestedActions(queryClient);
      resetPage();
      clearMuteForAllUndoTimeout();
    },
  );

  const undoNotifications = (
    <>
      <PopNotificationWithUndo
        isUndoAvailable={isDismissUndoAvailable}
        disabled={isRestoringDismiss}
        onUndo={onUndoDismiss}
      >
        <p>{t('actionDismissed', { count: SUGGESTED_ACTION_MUTE_DAYS })}</p>
      </PopNotificationWithUndo>
      <PopNotificationWithUndo
        isUndoAvailable={isDontRemindUndoAvailable}
        disabled={isRestoringDismiss}
        onUndo={onUndoDismiss}
      >
        <p>{t('youWontBeReminded')}</p>
      </PopNotificationWithUndo>
      <PopNotificationWithUndo
        isUndoAvailable={isMuteForAllUndoAvailable}
        disabled={isRestoringMuteForAll}
        onUndo={onUndoMuteForAllDeals}
      >
        <p>{t('actionMutedForAllDeals')}</p>
      </PopNotificationWithUndo>
    </>
  );

  return {
    undoNotifications,
    setDismissedAction,
    setDismissUndoTimeout,
    setDontRemindUndoTimeout,
    setMutedActionType,
    setMuteForAllDealsUndoTimeout,
    resetPage,
  };
};

export default useEnterpriseUndoActions;
